import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, MoreHorizontal, CircleCheck, Ban, PackageCheck } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatDate, formatQuantity } from "@/lib/format";
import { UnitCombobox } from "@/components/UnitCombobox";
import { Field, FormSection, LineCard, RequiredLegend } from "@/components/DocForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { DocStatusBadge } from "@/components/PoDnStatus";
import { useSupplierNames, supplierLabel } from "@/components/PurchaseOrdersTable";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";

type DeliveryNoteResponse = components["schemas"]["DeliveryNoteResponse"];
type SupplierResponse = components["schemas"]["SupplierResponse"];

interface LineDraft {
  // Present for an existing DN line (so the server reconciles by identity); absent for a newly added line.
  id?: string;
  // The caller's own back-pointer: never identifies the line (that's `id`) or affects matching.
  externalRef: string;
  description: string;
  sku: string;
  quantityReceived: string;
  unit: string;
}

const emptyLine = (): LineDraft => ({
  externalRef: "",
  description: "",
  sku: "",
  quantityReceived: "1",
  unit: "",
});

// Untouched rows are dropped on submit; a row with any content must validate, so nothing typed is discarded.
const isMeaningful = (l: LineDraft) =>
  Boolean(l.description.trim() || l.sku.trim() || l.externalRef.trim());

interface LineErrors {
  description: string | null;
  quantityReceived: string | null;
}

const hasLineError = (e: LineErrors) => e.description !== null || e.quantityReceived !== null;

// Zero is a receipt: a line that never turned up is received 0 so the match can attribute the shortfall.
// Blank is not zero (Number("") is 0), so the user must type 0. Negative is refused: a return is a credit note.
function quantityError(raw: string): string | null {
  const value = raw.trim();
  if (!value) return "Required.";
  const n = Number(value);
  if (!Number.isFinite(n)) return "Enter a number.";
  return n < 0 ? "Cannot be negative." : null;
}

// Mirrors CreateDeliveryNoteRequestValidator: DN number, at least one line, and every line needs a
// description and a quantity of zero or more.
function validate(dnNumber: string, lines: LineDraft[]) {
  const kept = lines.filter(isMeaningful);
  return {
    dnNumber: dnNumber.trim() ? null : "A delivery note needs a DN number.",
    noLines: kept.length === 0 ? "Add at least one line." : null,
    lines: lines.map<LineErrors>((l) => {
      if (!isMeaningful(l)) return { description: null, quantityReceived: null };
      return {
        description: l.description.trim() ? null : "Required.",
        quantityReceived: quantityError(l.quantityReceived),
      };
    }),
  };
}

// The API carries receivedAt as a full date-time ISO string; <input type="date"> wants YYYY-MM-DD.
const toDateInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "");
const toDateTimeBody = (value: string) => (value ? `${value}T00:00:00Z` : null);

const PAGE_SIZE = 20;

// Statuses the backend refuses to edit (DeliveryNote.NotEditable).
const TERMINAL_STATUSES = new Set(["Closed", "Cancelled"]);
const isEditable = (dn: DeliveryNoteResponse) => !TERMINAL_STATUSES.has(String(dn.status));

export interface DeliveryNotesTableProps {
  /** When set, only delivery notes for this supplier are listed. */
  supplierId?: string;
  onSelect?: (deliveryNoteId: string) => void;
  /** Bump this number to force a refetch (e.g. after the host creates a DN). */
  reloadSignal?: number;
  /** Reports the number of delivery notes currently shown, so the host can render a count. */
  onCountChange?: (count: number) => void;
}

export function DeliveryNotesTable({
  supplierId,
  onSelect,
  reloadSignal,
  onCountChange,
}: DeliveryNotesTableProps) {
  const api = useDolfinClient();
  const [notes, setNotes] = useState<DeliveryNoteResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DeliveryNoteResponse | null>(null);
  const [acting, setActing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Scoped to one supplier (the supplier page), the counterparty column would only repeat itself.
  const showSupplier = !supplierId;
  const supplierNames = useSupplierNames(showSupplier);

  const fetchNotes = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (supplierId) query.SupplierId = supplierId;
      if (cursor) query.Cursor = cursor;
      const { data, error: apiError } = await api.GET("/v1/delivery-notes", {
        params: { query },
      });
      if (apiError || !data) {
        setError(apiErrorMessage(apiError, "Couldn't load delivery notes."));
      } else {
        const rows = data.data ?? [];
        setNotes(rows);
        onCountChange?.(rows.length);
        const next = data.meta?.cursors?.next ?? null;
        setNextCursor(next);
        setHasMore(next !== null);
      }
      setLoading(false);
    },
    [api, supplierId, onCountChange],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchNotes();
    // reloadSignal is an explicit refetch trigger from the host.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNotes, reloadSignal]);

  const currentPage = cursorStack.length + 1;
  // Cursor that loaded the page currently in view (undefined on page 1); used to
  // refetch in place after an edit / close / cancel without jumping to page 1.
  const currentCursor = cursorStack[cursorStack.length - 1];

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchNotes(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchNotes(prevCursor);
  }

  async function transition(dn: DeliveryNoteResponse, action: "close" | "cancel") {
    setActing(true);
    const { error: apiError } =
      action === "close"
        ? await api.POST("/v1/delivery-notes/{id}/close", { params: { path: { id: dn.id! } } })
        : await api.POST("/v1/delivery-notes/{id}/cancel", { params: { path: { id: dn.id! } } });
    if (apiError) {
      toast.error(apiErrorMessage(apiError, `Couldn't ${action} the delivery note.`));
    } else {
      toast.success(`Delivery note ${action === "close" ? "closed" : "cancelled"}.`);
      await fetchNotes(currentCursor);
    }
    setActing(false);
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => fetchNotes(currentCursor)} />;
  }

  if (loading) {
    return <LoadingState label="Loading delivery notes..." />;
  }

  if (notes.length === 0) {
    return (
      <EmptyState icon={PackageCheck} title={supplierId ? "No delivery notes for this supplier" : "No delivery notes yet"}>
        Record goods as they arrive and Dolfin reconciles the quantities received against the
        supplier&apos;s bills.
      </EmptyState>
    );
  }

  return (
    <>
      <TableShell>
        <Table>
          <TableHeader>
            <HeaderRow>
              <TableHead className={TH}>{showSupplier ? "Supplier" : "Delivery note"}</TableHead>
              {showSupplier ? <TableHead className={TH}>DN number</TableHead> : null}
              <TableHead className={TH}>Status</TableHead>
              <TableHead className={TH}>Lines</TableHead>
              <TableHead className={TH}>Received</TableHead>
              <TableHead className="w-10"><span className="sr-only">Edit</span></TableHead>
              <TableHead className="w-10"><span className="sr-only">More actions</span></TableHead>
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {notes.map((dn) => {
              const recorded = `Recorded ${formatDate(dn.createdAt)}`;
              return (
                <TableRow key={dn.id} {...rowLink(onSelect ? () => onSelect(dn.id!) : undefined)}>
                  <TableCell className="max-w-[280px]">
                    {showSupplier ? (
                      <EntityCell
                        name={supplierLabel(dn.supplierId, supplierNames)}
                        fallback="No supplier yet"
                        secondary={recorded}
                      />
                    ) : (
                      <div className="min-w-0">
                        <div className="truncate font-mono text-sm font-semibold">{dn.dnNumber || "(no number)"}</div>
                        <div className="truncate text-xs text-muted-foreground">{recorded}</div>
                      </div>
                    )}
                  </TableCell>
                  {showSupplier ? (
                    <TableCell className="font-mono text-xs whitespace-nowrap">{dn.dnNumber || "(no number)"}</TableCell>
                  ) : null}
                  <TableCell>
                    <DocStatusBadge status={dn.status} />
                  </TableCell>
                  <TableCell className="max-w-72 whitespace-normal"><LineChips lines={dn.lines ?? []} /></TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {dn.receivedAt ? formatDate(dn.receivedAt) : "-"}
                  </TableCell>
                  <TableCell className="text-right" onClick={stop} onKeyDown={stop}>
                    {isEditable(dn) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit delivery note"
                        onClick={() => setEditing(dn)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right" onClick={stop} onKeyDown={stop}>
                    {isEditable(dn) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="More actions" disabled={acting}>
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => transition(dn, "close")}>
                            <CircleCheck className="size-4" /> Close
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => transition(dn, "cancel")}
                          >
                            <Ban className="size-4" /> Cancel
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableShell>

      <Pager
        page={currentPage}
        hasPrevious={currentPage > 1}
        hasNext={hasMore}
        onPrevious={handlePrevPage}
        onNext={handleNextPage}
      />

      <DeliveryNoteDialog
        open={editing !== null}
        editing={editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        onSaved={() => fetchNotes(currentCursor)}
      />
    </>
  );
}

// Clicks and Enter inside the action cells belong to the buttons, not to the row link around them.
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

type DeliveryNoteLine = NonNullable<DeliveryNoteResponse["lines"]>[number];

const MAX_VISIBLE_CHIPS = 3;
const MAX_DESCRIPTION_CHARS = 30;

function LineChips({ lines }: { lines: DeliveryNoteLine[] }) {
  const [expanded, setExpanded] = useState(false);

  if (lines.length === 0) {
    return <span className="text-muted-foreground">No lines</span>;
  }

  const visible = expanded ? lines : lines.slice(0, MAX_VISIBLE_CHIPS);
  const hidden = lines.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((line, i) => {
        const description = line.description ?? "";
        // Collapsed chips are trimmed to keep rows short; expanding shows every line in full.
        const shown =
          !expanded && description.length > MAX_DESCRIPTION_CHARS
            ? `${description.slice(0, MAX_DESCRIPTION_CHARS).trimEnd()}...`
            : description;
        return (
          <Badge key={line.id ?? i} variant="secondary" className="font-normal">
            {formatQuantity(line.quantityReceived, line.unit, line.unitOfMeasure)} x {shown || "(no description)"}
          </Badge>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          onKeyDown={stop}
        >
          +{hidden} more
        </button>
      )}
      {expanded && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          onKeyDown={stop}
        >
          Show less
        </button>
      )}
    </div>
  );
}

export function DeliveryNoteDialog({
  open,
  editing,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  editing: DeliveryNoteResponse | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (dn: DeliveryNoteResponse) => void;
}) {
  const api = useDolfinClient();
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [supplierId, setSupplierId] = useState<string>("");
  const [dnNumber, setDnNumber] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Validation is derived from the current values on every render but only *shown* once the user has
  // tried to submit, so an untouched blank form isn't already red.
  const [showErrors, setShowErrors] = useState(false);

  const isEdit = editing !== null;

  const kept = lines.filter(isMeaningful);
  const fieldErrors = validate(dnNumber, lines);
  const hasErrors =
    fieldErrors.dnNumber !== null ||
    fieldErrors.noLines !== null ||
    fieldErrors.lines.some(hasLineError);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setShowErrors(false);
    if (editing) {
      setSupplierId(editing.supplierId ?? "");
      setDnNumber(editing.dnNumber ?? "");
      setExternalRef(editing.externalRef ?? "");
      setReceivedAt(toDateInput(editing.receivedAt));
      const drafts = [...(editing.lines ?? [])]
        .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
        .map((l) => ({
          id: l.id,
          externalRef: l.externalRef ?? "",
          description: l.description ?? "",
          sku: l.sku ?? "",
          quantityReceived: String(l.quantityReceived ?? "0"),
          unit: l.unit ?? "",
        }));
      setLines(drafts.length > 0 ? drafts : [emptyLine()]);
    } else {
      setSupplierId("");
      setDnNumber("");
      setExternalRef("");
      setReceivedAt("");
      setLines([emptyLine()]);
    }
    api
      .GET("/v1/suppliers", { params: { query: { Limit: 100 } } })
      .then(({ data }) => setSuppliers(data?.data ?? []))
      .catch(() => setSuppliers([]));
  }, [open, editing, api]);

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setShowErrors(true);
    if (hasErrors) return;

    const cleanLines = kept;
    const lineBody = cleanLines.map((l, i) => ({
      externalRef: l.externalRef.trim() || null,
      description: l.description.trim(),
      sku: l.sku.trim() || null,
      // No `|| 0` fallback: validation has already rejected blank and non-numeric, so an honest 0 here
      // is one the user typed rather than one a coercion invented.
      quantityReceived: Number(l.quantityReceived),
      unit: l.unit.trim() || null,
      sortOrder: i,
    }));

    const header = {
      supplierId: supplierId || null,
      dnNumber: dnNumber.trim() || null,
      externalRef: externalRef.trim() || null,
      receivedAt: toDateTimeBody(receivedAt),
    };

    setSubmitting(true);
    const { data, error: apiError } =
      isEdit && editing
        ? await api.PUT("/v1/delivery-notes/{id}", {
            params: { path: { id: editing.id! } },
            body: {
              ...header,
              // Carry each existing line's id so the server updates in place / deletes omitted lines;
              // new lines have no id and are inserted.
              lines: lineBody.map((l, i) => ({ id: cleanLines[i]?.id ?? null, ...l })),
            },
          })
        : await api.POST("/v1/delivery-notes", {
            body: {
              ...header,
              lines: lineBody,
            },
          });

    if (apiError || !data) {
      const message = apiErrorMessage(
        apiError,
        isEdit ? "Failed to update delivery note." : "Failed to create delivery note.",
      );
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    toast.success(isEdit ? "Delivery note updated" : "Delivery note created");
    onSaved(data);
    setSubmitting(false);
    onOpenChange(false);
  }

  const totalReceived = kept.reduce((sum, l) => sum + (Number(l.quantityReceived) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-4xl">
        <form onSubmit={handleSubmit} className="flex max-h-[88vh] flex-col">
          <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12">
            <DialogTitle>{isEdit ? "Edit delivery note" : "Create delivery note"}</DialogTitle>
            <DialogDescription>
              Record what was physically received. Dolfin reconciles these quantities against the
              supplier&apos;s bill lines.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
            <FormSection title="Details">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field
                  id="dn-number"
                  label="DN number"
                  required
                  error={showErrors ? fieldErrors.dnNumber : null}
                >
                  <Input
                    id="dn-number"
                    value={dnNumber}
                    onChange={(e) => setDnNumber(e.target.value)}
                    placeholder="DN-1001"
                    aria-invalid={showErrors && fieldErrors.dnNumber !== null}
                  />
                </Field>

                <Field id="dn-supplier" label="Supplier" hint="Can be assigned later.">
                  <Select value={supplierId} onValueChange={setSupplierId}>
                    <SelectTrigger id="dn-supplier" className="w-full">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id!}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field id="dn-received-at" label="Received">
                  <Input
                    id="dn-received-at"
                    type="date"
                    value={receivedAt}
                    onChange={(e) => setReceivedAt(e.target.value)}
                  />
                </Field>

                <Field
                  id="dn-external-ref"
                  label="External reference"
                  hint="Your own id for this note. Echoed back untouched."
                  className="sm:col-span-2 lg:col-span-1"
                >
                  <Input
                    id="dn-external-ref"
                    value={externalRef}
                    onChange={(e) => setExternalRef(e.target.value)}
                    placeholder="Your own reference"
                  />
                </Field>
              </div>
            </FormSection>

            <FormSection
              title="Lines"
              description="SKU is the exact-match key against bill lines. Ext. ref is your own line id, stored and echoed back, never used for matching."
              actions={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                >
                  <Plus className="size-3.5" />
                  Add line
                </Button>
              }
            >
              <div className="space-y-3">
                {lines.map((line, i) => {
                  const lineError = fieldErrors.lines[i]!;
                  const show = showErrors;
                  return (
                    <LineCard
                      key={i}
                      index={i}
                      invalid={show && hasLineError(lineError)}
                      removeDisabled={lines.length === 1}
                      onRemove={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2.4fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_110px_130px]">
                        <Field
                          id={`dn-line-${i}-description`}
                          label="Description"
                          required
                          error={show ? lineError.description : null}
                        >
                          <Input
                            id={`dn-line-${i}-description`}
                            placeholder="What was delivered"
                            value={line.description}
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                            aria-invalid={show && lineError.description !== null}
                          />
                        </Field>
                        <Field id={`dn-line-${i}-sku`} label="SKU">
                          <Input
                            id={`dn-line-${i}-sku`}
                            placeholder="ABC-123"
                            value={line.sku}
                            onChange={(e) => updateLine(i, { sku: e.target.value })}
                          />
                        </Field>
                        <Field
                          id={`dn-line-${i}-external-ref`}
                          label="Ext. ref"
                        >
                          <Input
                            id={`dn-line-${i}-external-ref`}
                            placeholder="Your line id"
                            value={line.externalRef}
                            onChange={(e) => updateLine(i, { externalRef: e.target.value })}
                          />
                        </Field>
                        <Field
                          id={`dn-line-${i}-quantity`}
                          label="Received"
                          required
                          error={show ? lineError.quantityReceived : null}
                        >
                          <Input
                            id={`dn-line-${i}-quantity`}
                            type="number"
                            step="any"
                            min="0"
                            placeholder="0"
                            value={line.quantityReceived}
                            onChange={(e) => updateLine(i, { quantityReceived: e.target.value })}
                            aria-invalid={show && lineError.quantityReceived !== null}
                          />
                        </Field>
                        <Field id={`dn-line-${i}-unit`} label="Unit">
                          <UnitCombobox
                            placeholder="e.g. each"
                            value={line.unit}
                            onChange={(u) => updateLine(i, { unit: u ?? "" })}
                          />
                        </Field>
                      </div>
                    </LineCard>
                  );
                })}
              </div>

              {showErrors && fieldErrors.noLines && (
                <p className="text-sm text-destructive">{fieldErrors.noLines}</p>
              )}

              <div className="flex justify-end border-t pt-3 text-sm">
                <span className="text-muted-foreground">
                  {lines.length} line{lines.length === 1 ? "" : "s"} ·{" "}
                  <span className="font-medium text-foreground">
                    {formatQuantity(totalReceived)} received
                  </span>
                </span>
              </div>
            </FormSection>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter className="shrink-0 items-center border-t px-6 py-4 sm:justify-between">
            <RequiredLegend />
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Spinner className="size-4" />}
                {isEdit ? "Save changes" : "Create delivery note"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
